import * as io from "io-ts";
import React from "react";
import type { Project } from "../../api/useProjects";
import { useProjects } from "../../api/useProjects";
import type { Source } from "../../api/useSources";
import { useSources } from "../../api/useSources";
import { useUser } from "../../api/useUser";
import { useCanUserDo } from "../canUserDo";
import t from "../../i18n";
import createGlobalState from "@ui/lib/createGlobalState";
import useFilteredDatas from "@ui/lib/hooks/useFilteredDatas";
import useModal from "@ui/lib/hooks/useModal";
import { literalUnion, nullable } from "diagnostics-lib/io";
import Button from "@ui/Button";
import { FilterWrapper, NONE_LABEL, buildOptions, filterData, useSubStateSetter } from "@ui/Filter";
import FilterSelect from "@ui/Filter/FilterSelect";
import Reset from "@ui/Filter/Reset";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import Modal from "@ui/Modal";
import Section from "@ui/Section";
import SidePanelLayout from "@ui/SidePanel/Layout";
import TableCard from "@ui/Table/Card";
import CreateUpdateProjectsModalForm from "../Projects/CreateUpdateProjectsModalForm";
import PatchSnapshotsModalForm from "./PatchSnapshotsModalForm";
import SourceTable from "./Table";
import { UploadLine } from "@ui/IconV2/react";

const FILTER_KEYS = ["project"] as const;
const FilterKey = literalUnion(FILTER_KEYS);
type FilterKey = (typeof FILTER_KEYS)[number];

const FilteredValuesByFilterType = io.record(FilterKey, io.array(nullable(io.string)));
type FilteredValuesByFilterType = io.TypeOf<typeof FilteredValuesByFilterType>;

const DEFAULT_FILTER_VALUES: FilteredValuesByFilterType = {
  project: [],
};

const useFilteredValuesByFilterType = createGlobalState({
  storage: "session",
  key: "sourcesTableFilteredValues",
  type: FilteredValuesByFilterType,
  defaultValue: DEFAULT_FILTER_VALUES,
  scoped: false,
});

type SourceWithProjects = Source & { linkedProjects: Project[] };

const FILTER_METADATA = {
  project: {
    title: t("sources.selectFilter.project"),
    getOptions: (source: SourceWithProjects) =>
      source.linkedProjects.length > 0
        ? source.linkedProjects.map(p => ({
            label: p.name,
            value: p.id,
          }))
        : [{ label: NONE_LABEL, value: null }],
    withNone: true as const,
  },
};
const filterSources = filterData<SourceWithProjects, FilterKey, typeof FILTER_METADATA>(
  FILTER_METADATA,
);

const useSourcesSearch = createGlobalState({
  storage: "session",
  key: "sourcesSearch",
  type: io.string,
  defaultValue: "",
  scoped: false,
});

export const Sources = () => {
  const user = useUser();
  const [newSourceId, setNewSourceId] = React.useState<string>();
  const rawSources = useSources();
  const projects = useProjects();
  const sources: SourceWithProjects[] = React.useMemo(
    () =>
      rawSources.map(source => ({
        ...source,
        linkedProjects: projects.filter(p => p.sourceId === source.id),
      })),
    [rawSources, projects],
  );

  const {
    modal: isLoadDataModalOpen,
    openModal: openLoadDataModal,
    closeModal: closeLoadDataModal,
  } = useModal();

  const {
    modal: isCreateProjectModalOpen,
    openModal: openCreateProjectModal,
    closeModal: closeCreateProjectModal,
  } = useModal();

  const [search, setSearch] = useSourcesSearch({});
  const [filteredValues, setFilteredValues] = useFilteredValuesByFilterType({});
  const filteredSources = React.useMemo(
    () => filterSources(sources, filteredValues),
    [filteredValues, sources],
  );
  const [searchedSources, searchInProgress] = useFilteredDatas(
    filteredSources,
    search,
    source => source.id,
  );

  const projectOptions = React.useMemo(
    () =>
      buildOptions(
        [{ label: NONE_LABEL, value: null as string | null }].concat(
          sources.flatMap(source =>
            source.linkedProjects.map(p => ({
              label: p.name,
              value: p.id,
            })),
          ),
        ),
      ),
    [sources],
  );

  const canUserImport = useCanUserDo("SOURCE_WRITE");

  const actions = React.useMemo(
    () =>
      canUserImport ? (
        <Button
          type="button"
          variant="primary"
          leftIcon={<UploadLine />}
          onClick={openLoadDataModal}
        >
          {t("sources.button.import")}
        </Button>
      ) : null,
    [canUserImport, openLoadDataModal],
  );

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
    [setSearch],
  );

  return (
    <SidePanelLayout>
      <Section id="sources" title={t("sources.title")} actions={actions}>
        <TableCard
          title={null}
          headerLeft={
            <>
              <SearchInputV2
                data-cy="search-project"
                placeholder={t("commons.placeholder.search")}
                onChange={handleSearchChange}
                value={search}
              />
              <FilterWrapper>
                <FilterSelect
                  title={t("administration.quantities.table.header.project_name")}
                  options={projectOptions}
                  filtered
                  values={filteredValues["project"]}
                  setValues={useSubStateSetter(setFilteredValues, "project")}
                />
                <Reset
                  filterValues={filteredValues}
                  setFilterValues={setFilteredValues}
                  defaultFilterValues={DEFAULT_FILTER_VALUES}
                />
              </FilterWrapper>
            </>
          }
          headerRight={null}
        >
          <SourceTable user={user} sources={searchedSources} loading={searchInProgress} />
        </TableCard>
      </Section>
      <Modal
        title={t("sources.button.import")}
        isOpen={isLoadDataModalOpen}
        onRequestClose={closeLoadDataModal}
        size="l"
      >
        <PatchSnapshotsModalForm
          onRequestClose={closeLoadDataModal}
          onSourceUpdated={setNewSourceId}
          project={null}
          openCreateProjectModal={openCreateProjectModal}
        />
      </Modal>
      <Modal
        title={t("projects.modal.title.create_project")}
        isOpen={isCreateProjectModalOpen}
        onRequestClose={closeCreateProjectModal}
      >
        <CreateUpdateProjectsModalForm
          preselectedSource={newSourceId}
          onRequestClose={closeCreateProjectModal}
          okButtonName={t("modalForm.button.name.create")}
        />
      </Modal>
    </SidePanelLayout>
  );
};
